export const EMPTY_ANNOUNCEMENT_FORM = {
    badge: '',
    message: '',
    isActive: false,
    dismissible: true,
    cta: {
        label: '',
        href: '',
    },
};

function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function toPartialAnnouncement(input) {
    const source = input && typeof input === 'object' ? input : {};
    const rawCta =
        source.cta && typeof source.cta === 'object'
            ? source.cta
            : {
                  label: source.ctaLabel,
                  href: source.ctaHref,
              };

    return {
        id: source.id ?? null,
        badge: normalizeString(source.badge ?? source.title),
        message: normalizeString(source.message),
        isActive: Boolean(source.isActive ?? source.isEnabled),
        dismissible:
            source.dismissible === undefined
                ? EMPTY_ANNOUNCEMENT_FORM.dismissible
                : Boolean(source.dismissible),
        updatedAt: normalizeString(source.updatedAt),
        cta: {
            label: normalizeString(rawCta?.label),
            href: normalizeString(rawCta?.href),
        },
    };
}

function normalizeCta(input) {
    if (!input || typeof input !== 'object') {
        return null;
    }

    const label = normalizeString(input.label);
    const href = normalizeString(input.href);

    if (!label || !href) {
        return null;
    }

    return { label, href };
}

export function isAnnouncementHrefValid(href) {
    const normalizedHref = normalizeString(href);

    if (!normalizedHref) {
        return false;
    }

    return (
        normalizedHref.startsWith('/') ||
        normalizedHref.startsWith('#') ||
        normalizedHref.startsWith('http://') ||
        normalizedHref.startsWith('https://') ||
        normalizedHref.startsWith('mailto:') ||
        normalizedHref.startsWith('tel:')
    );
}

export function isAdminAnnouncementPath(pathname) {
    return pathname === '/admin' || pathname?.startsWith('/admin/');
}

export function normalizeAnnouncement(input) {
    const partial = toPartialAnnouncement(input);
    const cta = normalizeCta(partial.cta);

    if (!partial.badge && !partial.message) {
        return null;
    }

    return {
        id: partial.id,
        badge: partial.badge,
        message: partial.message,
        isActive: partial.isActive,
        dismissible: partial.dismissible,
        updatedAt: partial.updatedAt,
        cta,
    };
}

export function normalizeActiveAnnouncement(input) {
    const announcement = normalizeAnnouncement(input);

    if (!announcement?.isActive) {
        return null;
    }

    return announcement;
}

export function toAnnouncementFormState(input) {
    const announcement = toPartialAnnouncement(input);

    return {
        badge: announcement.badge,
        message: announcement.message,
        isActive: announcement.isActive,
        dismissible: announcement.dismissible,
        cta: {
            label: announcement.cta.label,
            href: announcement.cta.href,
        },
    };
}

export function buildAnnouncementPayload(input) {
    const formState = toAnnouncementFormState(input);
    const ctaLabel = normalizeString(formState.cta.label);
    const ctaHref = normalizeString(formState.cta.href);
    const hasCompleteCta = Boolean(ctaLabel && ctaHref);

    return {
        title: normalizeString(formState.badge),
        message: normalizeString(formState.message),
        isEnabled: Boolean(formState.isActive),
        dismissible: Boolean(formState.dismissible),
        ctaLabel: hasCompleteCta ? ctaLabel : null,
        ctaHref: hasCompleteCta ? ctaHref : null,
    };
}

export function validateAnnouncementForm(input) {
    const formState = toAnnouncementFormState(input);
    const errors = {};
    const message = normalizeString(formState.message);
    const ctaLabel = normalizeString(formState.cta.label);
    const ctaHref = normalizeString(formState.cta.href);

    if (!message) {
        errors.message = 'Message is required';
    }

    if (ctaLabel && !ctaHref) {
        errors.ctaHref = 'CTA link is required when CTA text is set';
    }

    if (ctaHref && !ctaLabel) {
        errors.ctaLabel = 'CTA text is required when CTA link is set';
    }

    if (ctaHref && !isAnnouncementHrefValid(ctaHref)) {
        errors.ctaHref = 'Use /path, #anchor, https://, mailto:, or tel:';
    }

    return errors;
}

export function buildAnnouncementDismissalKey(input) {
    const announcement = normalizeAnnouncement(input);

    if (!announcement) {
        return null;
    }

    const seed = [
        announcement.id || 'anonymous',
        announcement.updatedAt || '',
        announcement.badge,
        announcement.message,
        announcement.cta?.label ?? '',
        announcement.cta?.href ?? '',
    ].join('|');

    return `site-announcement:dismissed:${seed}`;
}

export function isAnnouncementExternalHref(href) {
    const normalizedHref = normalizeString(href);

    return (
        normalizedHref.startsWith('http://') ||
        normalizedHref.startsWith('https://') ||
        normalizedHref.startsWith('mailto:') ||
        normalizedHref.startsWith('tel:')
    );
}
